import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { MdAccessTime, MdEdit } from "react-icons/md";
import {
  BoardColumn,
  ProjectTask,
  TaskPriority,
  TaskType,
  TASK_PRIORITY_LABELS,
} from "../../../../interfaces/ProjectTask";
import { epicColor, EpicColor, NEUTRAL_COLOR } from "../../../../utils/epicColor";

interface Props {
  tasks: ProjectTask[];
  /** Colunas do board (vindas da API; inclui customizadas como "Blocked"). */
  columns: BoardColumn[];
  onEdit: (task: ProjectTask) => void;
  /** Chamado ao soltar um card: id da tarefa, nova coluna (status) e nova posição na coluna. */
  onMove: (taskId: number, newStatus: number, newIndex: number) => void;
  showProject?: boolean;
}

const priorityColor: Record<number, string> = {
  [TaskPriority.Low]: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  [TaskPriority.Medium]: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  [TaskPriority.High]: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  [TaskPriority.Urgent]: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const scheduleBadge = (task: ProjectTask) => {
  switch (task.scheduleState) {
    case "overdue":
      return { label: "Atrasada", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" };
    case "due_soon":
      return { label: "Vence em breve", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" };
    case "on_track":
      return { label: "No prazo", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" };
    case "done":
      return { label: "Concluída", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" };
    default:
      return null;
  }
};

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

const assigneeLabel = (t: ProjectTask) =>
  t.assignees && t.assignees.length > 0
    ? t.assignees.map((a) => a.name).filter(Boolean).join(", ")
    : t.assigneeName ?? "Sem responsável";

// Item achatado para render: cabeçalho de grupo (épico) ou card de tarefa com índice contíguo do DnD.
type ColEntry =
  | { kind: "header"; epic: ProjectTask; count: number; accent: EpicColor }
  | { kind: "card"; task: ProjectTask; index: number; accent: EpicColor };

const TaskBoard: React.FC<Props> = ({ tasks, columns, onEdit, onMove, showProject }) => {
  // Colunas visíveis no board, na ordem de posição (ex.: Cancelado fica fora por padrão).
  const boardColumns = useMemo(
    () => [...columns].filter((c) => c.showOnBoard).sort((a, b) => a.position - b.position),
    [columns]
  );

  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const titleById = useMemo(() => new Map(tasks.map((t) => [t.id, t.title])), [tasks]);

  // Sobe pela hierarquia até o épico ancestral (ou null se a tarefa não pertence a um épico).
  const epicOf = (t: ProjectTask): ProjectTask | null => {
    let cur: ProjectTask | undefined = t;
    let guard = 0;
    while (cur && guard++ < 12) {
      if (cur.type === TaskType.Epic) return cur;
      if (cur.parentTaskId == null) return null;
      cur = byId.get(cur.parentTaskId);
    }
    return null;
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    // destination.index é a posição na lista VISUAL (agrupada por épico), mas o estado/backend
    // ordenam por orderIndex puro. Traduz: acha o card que ficará DEPOIS do solto na visão
    // e usa a posição dele na lista ordenada por orderIndex — senão o card "pula" ao soltar.
    const taskId = Number(draggableId);
    const destStatus = Number(destination.droppableId);
    const visualCards = buildColumn(destStatus)
      .entries.filter((e): e is Extract<ColEntry, { kind: "card" }> => e.kind === "card")
      .map((e) => e.task)
      .filter((t) => t.id !== taskId); // DnD indexa a lista sem o card arrastado
    const nextCard = visualCards[destination.index]; // undefined = soltar no fim
    const flatSorted = [...visualCards].sort(
      (a, b) => a.orderIndex - b.orderIndex || a.id - b.id
    );
    const flatIndex = nextCard
      ? flatSorted.findIndex((t) => t.id === nextCard.id)
      : flatSorted.length;

    onMove(taskId, destStatus, flatIndex);
  };

  // Monta as entradas de uma coluna agrupadas por épico, com índices de Draggable CONTÍGUOS (0..n-1).
  const buildColumn = (status: number): { entries: ColEntry[]; count: number } => {
    const items = tasks
      .filter((t) => t.status === status && t.type !== TaskType.Epic)
      .sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id);

    const groups = new Map<number | "none", { epic: ProjectTask | null; items: ProjectTask[] }>();
    items.forEach((t) => {
      const epic = epicOf(t);
      const key = epic?.id ?? "none";
      const g = groups.get(key) ?? { epic, items: [] };
      g.items.push(t);
      groups.set(key, g);
    });

    // Ordena: grupos de épico por orderIndex/título; "sem épico" por último.
    const ordered = Array.from(groups.values()).sort((a, b) => {
      if (!a.epic) return 1;
      if (!b.epic) return -1;
      return a.epic.orderIndex - b.epic.orderIndex || a.epic.title.localeCompare(b.epic.title);
    });

    const entries: ColEntry[] = [];
    let di = 0; // índice contíguo dos Draggables na coluna
    ordered.forEach((g) => {
      const accent = epicColor(g.epic?.id ?? null);
      if (g.epic) entries.push({ kind: "header", epic: g.epic, count: g.items.length, accent });
      g.items.forEach((task) => entries.push({ kind: "card", task, index: di++, accent }));
    });

    return { entries, count: items.length };
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-3">
        {boardColumns.map((column) => {
          const status = column.id;
          const { entries, count } = buildColumn(status);
          return (
            <Droppable droppableId={String(status)} key={status}>
              {(dropProvided, dropSnapshot) => (
                <div
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  className={`flex w-72 shrink-0 flex-col rounded-2xl p-3 transition-colors duration-200 ${
                    dropSnapshot.isDraggingOver
                      ? "bg-brand-50 ring-2 ring-brand-300 dark:bg-navy-900/70"
                      : "bg-gray-50 dark:bg-navy-900"
                  }`}
                >
                  <div className="mb-3 flex items-center justify-between gap-2 px-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: column.color ?? "#94a3b8" }}
                        aria-hidden="true"
                      />
                      <span className="truncate text-sm font-bold text-navy-700 dark:text-white" title={column.name}>
                        {column.name}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700 dark:bg-navy-800 dark:text-gray-300">
                      {count}
                    </span>
                  </div>

                  <div className="flex min-h-[60px] flex-1 flex-col gap-2">
                    {entries.map((entry) =>
                      entry.kind === "header" ? (
                        <div
                          key={`epic-${entry.epic.id}`}
                          className="mt-1 flex items-center gap-2 rounded-lg px-2 py-1 first:mt-0"
                          style={{ background: entry.accent.tint }}
                        >
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                            style={{ background: entry.accent.solid }}
                          />
                          <span
                            className="min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-wide"
                            style={{ color: entry.accent.text }}
                            title={entry.epic.title}
                          >
                            {entry.epic.title}
                          </span>
                          <span
                            className="shrink-0 rounded-full bg-white/70 px-1.5 text-[10px] font-bold"
                            style={{ color: entry.accent.text }}
                          >
                            {entry.count}
                          </span>
                        </div>
                      ) : (
                        <TaskCard
                          key={entry.task.id}
                          task={entry.task}
                          index={entry.index}
                          accent={entry.accent}
                          grouped={entry.accent !== NEUTRAL_COLOR}
                          onEdit={onEdit}
                          showProject={showProject}
                          parentTitle={
                            entry.task.parentTaskId != null
                              ? titleById.get(entry.task.parentTaskId)
                              : undefined
                          }
                        />
                      )
                    )}
                    {dropProvided.placeholder}

                    {count === 0 && !dropSnapshot.isDraggingOver && (
                      <div className="rounded-xl border border-dashed border-gray-200 py-6 text-center text-xs text-gray-700 dark:border-white/10 dark:text-gray-400">
                        Arraste cards para cá
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};

// ── Card ────────────────────────────────────────────────────────────────────────
const TaskCard: React.FC<{
  task: ProjectTask;
  index: number;
  accent: EpicColor;
  grouped: boolean;
  onEdit: (t: ProjectTask) => void;
  showProject?: boolean;
  parentTitle?: string;
}> = ({ task, index, accent, grouped, onEdit, showProject, parentTitle }) => {
  const badge = scheduleBadge(task);
  return (
    <Draggable draggableId={String(task.id)} index={index} key={task.id}>
      {(dragProvided, dragSnapshot) => {
        const card = (
        <div
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          {...dragProvided.dragHandleProps}
          onClick={(e) => {
            // O dnd chama preventDefault() no click residual de um drag; sem o guard,
            // todo arraste abriria o modal de edição.
            if (!e.defaultPrevented) onEdit(task);
          }}
          style={{
            ...dragProvided.draggableProps.style,
            borderLeft: `4px solid ${accent.solid}`,
            ...(grouped ? { marginLeft: 6 } : {}),
            ...(dragSnapshot.isDragging
              ? { transform: `${dragProvided.draggableProps.style?.transform ?? ""} rotate(3deg)` }
              : {}),
          }}
          className={`cursor-grab rounded-xl bg-white p-3 transition-shadow active:cursor-grabbing dark:bg-navy-800 ${
            dragSnapshot.isDragging ? "shadow-2xl ring-2 ring-brand-400" : "shadow-sm hover:shadow-md"
          }`}
        >
          {showProject && task.projectName && (
            <span className="mb-1 inline-block rounded-md bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
              {task.projectName}
            </span>
          )}
          <div className="mb-1 flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">{task.title}</p>
            <div className="flex shrink-0 items-center gap-1">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${priorityColor[task.priority]}`}>
                {TASK_PRIORITY_LABELS[task.priority]}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="rounded-md p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:hover:bg-navy-700"
                aria-label={`Editar tarefa ${task.title}`}
                title="Editar tarefa"
              >
                <MdEdit className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {parentTitle && (
            <div className="mb-1 truncate text-[10px] font-medium text-gray-700 dark:text-gray-300">
              ↳ {parentTitle}
            </div>
          )}

          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {badge && (
              <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
            )}
            {task.estimatedHours != null && (
              <span className="flex items-center gap-0.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-navy-900 dark:text-gray-300">
                <MdAccessTime className="h-3 w-3" aria-hidden="true" /> {task.estimatedHours}h
              </span>
            )}
            {!!task.subtaskCount && task.subtaskCount > 0 && (
              <span className="rounded-md bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                {task.subtaskCount} subtarefa{task.subtaskCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 text-[11px] text-gray-700 dark:text-gray-300">
            <span className="min-w-0 truncate" title={assigneeLabel(task)}>{assigneeLabel(task)}</span>
            <span className={`shrink-0 tabular-nums ${task.isOverdue ? "font-semibold text-red-600 dark:text-red-400" : ""}`}>{formatDate(task.dueDate)}</span>
          </div>
        </div>
        );
        // Durante o arraste, renderiza o card via portal no <body> para escapar de
        // qualquer ancestral com `transform` (ex.: animate-fade-up), que quebraria
        // o posicionamento `position: fixed` do @hello-pangea/dnd.
        return dragSnapshot.isDragging ? createPortal(card, document.body) : card;
      }}
    </Draggable>
  );
};

export default TaskBoard;
